import { useEffect, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { connect } from "react-redux";

import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { useLoading } from "app/custom-hooks/loadingHook";
import { SegmentsSingleMarketingChannelsGraphChart } from "app/pages/segments/mmx/SegmentsSingleMarketingGraphChart";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IBaseSingleRequestParams, ICustomSegmentAccount } from "app/services/segments/segmentsApiService";
import SegmentsApiService from "app/services/segments/segmentsApiService";

import { SegmentsMmxSingleOverview } from "./SegmentsMmxSingleOverview";
import { MarketingChannelsBoxContainer } from "./styledComponents";

interface ISegmentsSingleMarketingChannelsContainer {
    organizationSegments: ICustomSegmentAccount[];
}

const SegmentsSingleMarketingChannelsContainer: React.FC<ISegmentsSingleMarketingChannelsContainer> = (props) => {
    const { organizationSegments } = props;
    const { params } = useCurrentStateAndParams();
    const apiParams = useMemo(() => proNavigator.getApiParams(params), [params]);
    const { id, webSource } = params;

    const segmentData = useMemo(() => {
        return SegmentsUtils.getSegmentById({ segments: organizationSegments }, id) as ICustomSegmentAccount;
    }, [organizationSegments, id]);

    const { segmentsApiService } = useMemo(
        () => ({
            segmentsApiService: new SegmentsApiService(),
        }),
        [],
    );
    const [segmentMmxGraphChartData, segmentMmxGraphDataOps] = useLoading();

    // This is the BarChart Data call, but because of performance issues we transform the Data retuning from Graph call to fit bar chart
    // const isMmxBarChartLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(
    //     segmentMmxBarChartData.state,
    // );

    const isGraphChartDataLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(
        segmentMmxGraphChartData.state,
    );

    useEffect(() => {
        if (!segmentData || segmentData.isLocked) {
            if (segmentData?.isLocked) {
                proNavigator.go("digitalsuite_segments-homepage");
            }
            return null;
        }
    }, [segmentData]);

    useEffect(() => {
        segmentData &&
            segmentMmxGraphDataOps.load(() =>
                segmentsApiService.getCustomSegmentMarketingMixGraphData({
                    ...apiParams,
                    id: undefined,
                    segmentsIds: id,
                    keys: "null",
                    webSource,
                    includeSubDomains: true,
                    lastUpdated: segmentData.lastUpdated,
                } as IBaseSingleRequestParams),
            );
    }, [segmentData?.lastUpdated, segmentsApiService, apiParams, id, webSource]);

    const barChartData = useMemo(() => {
        return transformGraphMMXDataToBarChartData(segmentMmxGraphChartData.data, id);
    }, [segmentMmxGraphChartData.data, id]);

    return (
        <MarketingChannelsBoxContainer data-automation="SegmentsSingleMarketingChannelsContainer">
            <SegmentsMmxSingleOverview
                params={params}
                data={barChartData}
                isLoading={isGraphChartDataLoading}
            />
            <SegmentsSingleMarketingChannelsGraphChart
                params={params}
                data={segmentMmxGraphChartData.data}
                isLoading={isGraphChartDataLoading}
                segmentData={segmentData}
            />
        </MarketingChannelsBoxContainer>
    );
};

function mapStateToProps(store) {
    const {
        segmentsModule: { customSegmentsMeta },
    } = store;
    return {
        organizationSegments: customSegmentsMeta?.AccountSegments,
    };
}

export default connect(mapStateToProps, undefined)(SegmentsSingleMarketingChannelsContainer);

interface ISegmentsMMXDataPoint {
    Value: number;
    Percentage: number;
    Confidence: number;
}

interface ISegmentsMMXBarChartEngagementVerticals {
    Visits: ISegmentsMMXBarChannelData;
    Duration: ISegmentsMMXBarChannelData;
    PagesPerVisit: ISegmentsMMXBarChannelData;
    BounceRate: ISegmentsMMXBarChannelData;
    PageViews: ISegmentsMMXBarChannelData;
}

interface ISegmentsMMXGraphChartEngagementVerticals {
    Visits: ISegmentsMMXGraphChannelsData;
    Duration: ISegmentsMMXGraphChannelsData;
    PagesPerVisit: ISegmentsMMXGraphChannelsData;
    BounceRate: ISegmentsMMXGraphChannelsData;
    PageViews: ISegmentsMMXGraphChannelsData;
}

interface ISegmentsMMXBarChannelData {
    "Internal Referrals": ISegmentsMMXDataPoint;
    "Paid Search": ISegmentsMMXDataPoint;
    "Organic Search": ISegmentsMMXDataPoint;
    Direct: ISegmentsMMXDataPoint;
    Social: ISegmentsMMXDataPoint;
    Referrals: ISegmentsMMXDataPoint;
    Email: ISegmentsMMXDataPoint;
    "Display Ads": ISegmentsMMXDataPoint;
}

interface ISegmentsMMXGraphChannelsData {
    "Internal Referrals": ISegmentsMMXGraphSingleChannelData;
    "Paid Search": ISegmentsMMXGraphSingleChannelData;
    "Organic Search": ISegmentsMMXGraphSingleChannelData;
    Direct: ISegmentsMMXGraphSingleChannelData;
    Social: ISegmentsMMXGraphSingleChannelData;
    Referrals: ISegmentsMMXGraphSingleChannelData;
    Email: ISegmentsMMXGraphSingleChannelData;
    "Display Ads": ISegmentsMMXGraphSingleChannelData;
}

interface ISegmentsMMXGraphSingleChannelData {
    Breakdown: { Key: string; Value: ISegmentsMMXDataPoint }[];
    Total: ISegmentsMMXDataPoint;
}

export interface ISegmentsMMXGraphData {
    [segmentId: string]: {
        Monthly: ISegmentsMMXGraphChartEngagementVerticals;
        Weekly: ISegmentsMMXGraphChartEngagementVerticals;
        Meta?: {
            SegmentShare?: number;
        };
    };
}

export interface ISegmentsMMXBarChartData {
    Data: {
        [segmentId: string]: ISegmentsMMXBarChartEngagementVerticals;
    };
}

const transformGraphMMXDataToBarChartData = (
    graphData: ISegmentsMMXGraphData,
    segmentId: string,
): ISegmentsMMXBarChartData => {
    return !graphData
        ? undefined
        : {
              Data: {
                  [segmentId]: {
                      Visits: {
                          "Internal Referrals": graphData?.[segmentId]?.Monthly?.Visits?.["Internal Referrals"]?.Total,
                          "Paid Search": graphData?.[segmentId]?.Monthly?.Visits?.["Paid Search"]?.Total,
                          "Organic Search": graphData?.[segmentId]?.Monthly?.Visits?.["Organic Search"]?.Total,
                          Direct: graphData?.[segmentId]?.Monthly?.Visits?.Direct?.Total,
                          Social: graphData?.[segmentId]?.Monthly?.Visits?.Social?.Total,
                          Referrals: graphData?.[segmentId]?.Monthly?.Visits?.Referrals?.Total,
                          Email: graphData?.[segmentId]?.Monthly?.Visits?.Email?.Total,
                          "Display Ads": graphData?.[segmentId]?.Monthly?.Visits?.["Display Ads"]?.Total,
                      },
                      Duration: undefined,
                      PagesPerVisit: undefined,
                      BounceRate: undefined,
                      PageViews: undefined,
                  },
              },
          };
};
